// src/pages/admin/Reports.jsx
import React, { useEffect, useState } from "react";
import { getDesigners, getOrders, getCommissions } from "../../lib/db";
import { Btn, SectionLabel, Spinner, fmt$, fmtDate } from "../../components/UI";

function csvDownload(filename, rows, headers) {
  const lines = [headers.join(","), ...rows.map(r => headers.map(h => `"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const [designers, setDesigners] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([getDesigners(), getOrders(), getCommissions()]).then(([d,o,c]) => {
      setDesigners(d); setOrders(o); setCommissions(c); setLoading(false);
    });
  }, []);

  const downloadDesigners = () => {
    csvDownload("david-meyer-designers.csv", designers.map(d => ({
      Name: d.name, Firm: d.firm, Email: d.email, Tier: d.tier,
      Rate: `${d.commissionRate}%`, "YTD Sales": d.ytdSales,
      "YTD Earned": d.ytdEarned, "Pending Payout": d.pendingPayout,
      "Total Paid": d.totalPaid, Status: d.status,
    })), ["Name","Firm","Email","Tier","Rate","YTD Sales","YTD Earned","Pending Payout","Total Paid","Status"]);
  };

  const downloadOrders = () => {
    csvDownload("david-meyer-orders.csv", orders.map(o => ({
      "Order #": o.orderNumber || o.id, Designer: o.designerName,
      Client: o.clientName, Description: o.description,
      Amount: o.amount, Commission: o.commission,
      Rate: `${o.commissionRate}%`, Status: o.status,
    })), ["Order #","Designer","Client","Description","Amount","Commission","Rate","Status"]);
  };

  const downloadCommissions = () => {
    csvDownload("david-meyer-payouts.csv", commissions.map(c => {
      const d = designers.find(d => d.id === c.designerUid);
      return { Designer: d?.name || c.designerUid, Amount: c.amount, Status: c.status };
    }), ["Designer","Amount","Status"]);
  };

  const totalSales   = designers.reduce((s,d) => s+(d.ytdSales||0), 0);
  const totalEarned  = designers.reduce((s,d) => s+(d.ytdEarned||0), 0);
  const totalPending = designers.reduce((s,d) => s+(d.pendingPayout||0), 0);
  const totalPaid    = designers.reduce((s,d) => s+(d.totalPaid||0), 0);

  if (loading) return <Spinner />;

  const ReportCard = ({ title, desc, stat, statLabel, onDownload }) => (
    <div style={{ background:"var(--warm-white)", border:"1px solid var(--border)", borderRadius:4, padding:22, marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:400, marginBottom:4 }}>{title}</div>
          <div style={{ fontSize:12, color:"var(--mid)" }}>{desc}</div>
          {stat && <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:300, color:"var(--gold)", marginTop:10 }}>{stat} <span style={{ fontSize:12, fontFamily:"'Jost',sans-serif", fontWeight:300, color:"var(--mid)" }}>{statLabel}</span></div>}
        </div>
        <Btn variant="gold" size="sm" onClick={onDownload}>↓ Download CSV</Btn>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ padding:"36px 48px 0" }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:36, fontWeight:300 }}>Reports</h1>
        <p style={{ fontSize:13, color:"var(--mid)", marginTop:4 }}>Export program data as CSV</p>
      </div>
      <div style={{ padding:"24px 48px 48px" }}>

        <div style={{ background:"var(--charcoal)", borderRadius:4, padding:"24px 28px", marginBottom:28, display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:24 }}>
          {[["Total Sales YTD", fmt$(totalSales)], ["Total Commissions", fmt$(totalEarned)], ["Paid Out", fmt$(totalPaid)], ["Still Pending", fmt$(totalPending)]].map(([l,v]) => (
            <div key={l}>
              <div style={{ fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(245,240,232,0.45)", marginBottom:6 }}>{l}</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:300, color:"var(--gold-light)" }}>{v}</div>
            </div>
          ))}
        </div>

        <SectionLabel>Available Reports</SectionLabel>
        <ReportCard title="Designer Summary" desc={`${designers.length} designers · tiers, rates, YTD totals`} stat={designers.length} statLabel="designers" onDownload={downloadDesigners} />
        <ReportCard title="Order History" desc={`${orders.length} orders · all referrals and commissions`} stat={orders.length} statLabel="orders" onDownload={downloadOrders} />
        <ReportCard title="Payout History" desc={`${commissions.length} payments recorded`} stat={commissions.length} statLabel="payments" onDownload={downloadCommissions} />
      </div>
    </div>
  );
}
